/**
 * Wearing the brand: one palette, one set of faces, written onto a document as CSS variables.
 *
 * ## The three copies this replaces
 *
 * `store`, `subscribe` and `checkout` each had a `theme.ts`, and the three were the same file with a
 * different prefix — the same eleven colour roles, the same hex validation, the same WCAG luminance
 * maths deriving a readable foreground, the same `@font-face` emitter, the same `<style>` injection.
 * `checkout`'s carried a comment saying it derives the button foreground *"identically to `store`, so
 * one purchase has one button"*, which is an accurate description of a problem: the sentence was true
 * only for as long as somebody kept editing both.
 *
 * They also each got the FONT ROLES wrong at least once, in different directions, and one of those is
 * in `checkout`'s docstring: `primary_font_family` is the display face and was being written into the
 * body variable, so a page set its paragraphs in Anton.
 *
 * ## What stays per-app, and why it is an argument rather than a constant
 *
 * Two things, both passed in:
 *
 * - **`prefix`** — the variable namespace. `store`'s stylesheets say `var(--store-primary)` in fifty
 *   places and `subscribe`'s say `var(--subscribe-primary)`; renaming those is churn with no reader.
 *   More importantly a page may hold BOTH — a checkout widget on a storefront — and one namespace for
 *   two documents is how a nested surface silently repaints its host.
 * - **`fonts`** — which brand role fills which page role. A page thinks in `display` and `body`; a
 *   brand thinks in `primary` and `secondary`. Mapping between them is a decision about a document,
 *   which is why `api/public.py` answers the brand's own names and refuses to guess.
 *
 * ## The foreground on a filled colour is DERIVED, never chosen
 *
 * ⚠️ This is a real, shipped accessibility failure. Every call to action on `store` was
 * `color: var(--store-text)` — the brand's page text, off-white — on a fill of `var(--store-primary)`.
 * On quantumalex that is #f5f5f5 on #00c853: about 2.2:1, against the 4.5:1 ordinary text needs. The
 * most important word on the page was the least legible thing on it, and no amount of brand fidelity
 * makes that acceptable — a bright accent cannot carry light text.
 *
 * So `<prefix>-on-primary` is computed from the fill's own luminance: whichever extreme is further from
 * it wins, which is the rule a designer applies by eye and the reason a green button gets black type
 * while an indigo one keeps white.
 *
 * ## Every value is checked, because a brand record is a place somebody types
 *
 * These land in a `style` attribute and in a stylesheet. A colour must be a plain hex, a family name
 * must look like a family name, a face url must be https — anything else falls back. The record
 * reaches here through an API that validates, so this is the second lock rather than the first.
 */

import type { Brand, Font } from './types';
import { COLOR_FIELDS, LEGACY_COLOR_FIELDS } from './types';

/** A brand's four typographic roles, as the record names them. */
/** ⚠️ Three roles since 2026-10-02, when the brand's handwriting face was retired. */
export type BrandFontRole = 'primary' | 'secondary' | 'mono';

/** The resolved faces, keyed by brand role — `Brand['fonts']`, and what `GET /v1/public` answers. */
export type BrandFonts = Partial<Record<BrandFontRole, Font | null>>;

/**
 * What theming needs of a brand: the eleven colours and the resolved faces.
 *
 * Every field optional, because a caller may be painting from a cache that predates a field, from an
 * unsaved brand, or from nothing at all on the first frame. A full {@link Brand} satisfies it.
 */
export type ThemeSource = Partial<
  Pick<Brand, (typeof COLOR_FIELDS)[number] | (typeof LEGACY_COLOR_FIELDS)[number]>
> & {
  fonts?: BrandFonts | null;
};

/** The neutral palette a page paints with before the brand lands. Dark, because most public pages are. */
export const FALLBACK_PALETTE: Readonly<Record<string, string>> = {
  // The six live roles (2026-10-04). Same hexes as the API's `DEFAULT_COLORS` — duplicated on
  // purpose, because this half has to work with no network at all.
  primary: '#4f46e5',
  secondary: '#f43f5e',
  dark: '#08090c',
  white: '#f4f6fa',
  grey: '#98a2b3',
  error: '#f43f5e',
  // The retired six, kept because the API still answers them and three storefronts still name them
  // in their stylesheets. See `brandVariables`.
  background: '#08090c',
  'background-alt': '#0d1016',
  surface: '#141925',
  'surface-alt': '#1a2030',
  text: '#f4f6fa',
  muted: '#98a2b3',
  border: '#232b3a',
  success: '#4f46e5',
  danger: '#f43f5e',
};

/** The generic stack each brand role falls through to when the brand fills none. */
const FALLBACK_STACKS: Readonly<Record<BrandFontRole, string>> = {
  primary: 'Georgia, serif',
  secondary: 'system-ui, sans-serif',
  mono: 'ui-monospace, monospace',
};

/**
 * How a document maps its own font names onto the brand's roles.
 *
 * The key is the suffix after `--<prefix>-font`, so `{ title: 'primary' }` writes
 * `--x-font-title`. ⚠️ An EMPTY key is allowed and writes the bare `--<prefix>-font`, which is what
 * `checkout`'s stylesheet has always called its body face.
 */
export type FontAliases = Readonly<Record<string, BrandFontRole>>;

/** The identity mapping: the brand's four roles under their own names. What a console wants. */
export const BRAND_FONT_ALIASES: Readonly<Record<BrandFontRole, BrandFontRole>> = {
  primary: 'primary',
  secondary: 'secondary',
  mono: 'mono',
};

/**
 * The mapping a public **page** wants: a display face, a body face, a monospace one.
 *
 * ⚠️ `display` is `primary` and `body` is `secondary`, and that is the pairing `checkout` shipped
 * backwards — headings are what a brand picks a voice for, so the record's first choice is the loud
 * one. A page that sets its paragraphs in the display face is unreadable in exactly the way nobody
 * reports as a bug.
 */
export const PAGE_FONT_ALIASES: Readonly<{
  display: BrandFontRole;
  body: BrandFontRole;
  mono: BrandFontRole;
}> = {
  display: 'primary',
  body: 'secondary',
  mono: 'mono',
};

export interface ThemeOptions {
  /** The variable namespace, without dashes — `store` gives `--store-primary`. Defaults to `brand`. */
  prefix?: string;
  /** Which brand role fills which page role. Defaults to {@link BRAND_FONT_ALIASES}. */
  fonts?: FontAliases;
  /** Overrides for {@link FALLBACK_PALETTE}, keyed by role name without the prefix. */
  fallbacks?: Readonly<Record<string, string>>;
  /**
   * Which VARIABLE each colour field writes, when the derived name is not the one a stylesheet uses.
   *
   * Keyed by the brand field (`background_color`), valued with the suffix after `--<prefix>-`. The
   * default drops `_color` and swaps underscores for dashes — `background_color` becomes
   * `--<prefix>-background` — and `store` and `subscribe` use exactly that.
   *
   * ⚠️ `checkout` does not: its stylesheet has said `var(--x-bg)` and `var(--x-text)` since before any
   * of this was shared, in a few hundred places across inline styles and one CSS file. Renaming those
   * would be churn with no reader, and a mapping passed in one place is how that stays a detail of
   * that app rather than a second implementation of this one.
   */
  colors?: Readonly<Record<string, string>>;
  /**
   * Which VARIABLE each DERIVED surface writes — `surface`, `surface-alt`, `background-alt`,
   * `hairline` — when the default name is not what the stylesheet says.
   *
   * These are not brand fields: the palette has no surface role since 2026-10-03, so a panel and a
   * hairline are blended from `dark`, `white` and `grey`. They are emitted on every page regardless,
   * so a stylesheet can ask for a card without naming a role that no longer exists.
   */
  surfaces?: Readonly<Record<string, string>>;
  /** The id of the `<style>` element the `@font-face` rules are written into. */
  styleId?: string;
}

/** One colour, or the fallback. Anything that is not a plain hex is refused rather than passed on. */
function colour(value: unknown, fallback: string): string {
  const candidate = typeof value === 'string' ? value.trim() : '';
  return /^#[0-9a-fA-F]{3,8}$/.test(candidate) ? candidate : fallback;
}

/** A hex colour as its three channels, 0-255. Accepts `#abc` and `#aabbcc`, ignores any alpha. */
function channels(hex: string): [number, number, number] {
  const raw = hex.replace('#', '');
  const parts =
    raw.length === 3 || raw.length === 4
      ? [...raw.slice(0, 3)].map((channel) => channel + channel)
      : [raw.slice(0, 2), raw.slice(2, 4), raw.slice(4, 6)];
  const [r, g, b] = parts.map((part) => Number.parseInt(part, 16) || 0);
  return [r ?? 0, g ?? 0, b ?? 0];
}

/** Relative luminance, per WCAG 2.1 — the sRGB channels linearised and weighted. */
function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

/** The WCAG 2.1 contrast ratio between two colours: 1 (identical) to 21 (black on white). */
function contrast(one: string, two: string): number {
  const first = luminance(one);
  const second = luminance(two);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/**
 * The text colour to print ON a filled brand colour: whichever of the page's two extremes reads.
 *
 * ⚠️ **The two ratios are computed and compared.** They are not compared against a luminance
 * threshold, and the difference is not academic: `store`, `subscribe` and `checkout` each shipped
 * `luminance(fill) > 0.45 ? dark : light`, and quantumalex's accent is #00c853 — luminance 0.42, so
 * that rule chose the LIGHT ink. Off-white on that green is 2.2:1. Black on it is 9.4:1.
 *
 * The threshold was not merely mis-tuned: for a mid-tone fill against near-black and near-white the
 * crossover is at luminance ≈ 0.18, not 0.45, so every fill between those two numbers got the less
 * legible of the two choices. The fix is to stop guessing where the crossover is and measure — which
 * also stays correct for a page whose two extremes are not near-black and near-white at all.
 *
 * A tie goes to `light`, which only happens when the fill is equidistant from both.
 */
export function readableOn(fill: string, dark: string, light: string): string {
  return contrast(fill, dark) > contrast(fill, light) ? dark : light;
}

/** The CSS stack for one brand role: the catalog's resolved value, or a generic family. */
function stackFor(fonts: BrandFonts | null | undefined, role: BrandFontRole): string {
  const stack = (fonts?.[role]?.css_stack ?? '').trim();
  // Interpolated into a style attribute, so it is checked rather than trusted: family names, quotes,
  // commas and spaces only.
  if (!stack || !/^[\w\s,'"-]{1,240}$/.test(stack)) return FALLBACK_STACKS[role];
  return stack;
}

/**
 * Every CSS variable a document draws itself with, from one brand.
 *
 * ⚠️ The brand record's own `*_font_family` fields are NOT consulted. They hold SLUGS (`anton`,
 * `jetbrains-mono`) and a slug in a `font-family` declaration names a family that does not exist — the
 * browser skips it silently and uses the next entry, so the page looks like the fallback was
 * deliberate. `store` shipped that for weeks. Only the catalog knows the real name, which is why
 * `GET /v1/public` resolves it and why this reads `fonts` instead.
 */
/**
 * How far a surface sits from the dark, towards the white — and a hairline towards the grey.
 *
 * ⚠️ **The same four numbers as the Python connector's `surfaces.py`, and they are measured rather
 * than chosen**: each was solved from the hex both live tenants actually stored on 2026-10-03, the
 * day the palette dropped `surface_color`, `surface_alt_color`, `background_alt_color` and
 * `border_color`. Duplicated here rather than imported because this package has **zero
 * dependencies** — that is the whole reason it exists, and a storefront cannot reach into a Python
 * lib. If one side is retuned the other has to move with it; there is no mechanism that enforces
 * that, which is why the numbers are written out and named in both places.
 */
export const SURFACE_LIFT = 0.059;
export const SURFACE_ALT_LIFT = 0.099;
export const BACKGROUND_ALT_LIFT = 0.026;
export const HAIRLINE_LIFT = 0.206;

/**
 * `source` moved `amount` of the way towards `towards`, as `#rrggbb`.
 *
 * Uses this module's own `channels`, which is defined below and tolerates `#abc` — a brand colour
 * arrives normalised, but a `fallbacks` override is hand-written and does not have to be.
 */
export function mix(source: string, towards: string, amount: number): string {
  const start = channels(source);
  const end = channels(towards);
  const ratio = Math.min(1, Math.max(0, amount));
  return (
    '#' +
    start
      .map((channel, index) =>
        Math.round(channel + ((end[index] as number) - channel) * ratio)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

export function brandVariables(
  source: ThemeSource | null | undefined,
  {
    prefix = 'brand',
    fonts = BRAND_FONT_ALIASES,
    fallbacks = {},
    colors = {},
    surfaces = {},
  }: ThemeOptions = {},
): Record<string, string> {
  const fallback = (role: string): string => fallbacks[role] ?? FALLBACK_PALETTE[role] ?? '#000000';
  const variables: Record<string, string> = {};
  // ⚠️ BOTH lists. The retired roles are still on the wire and three storefronts still name them in
  // their stylesheets — `checkout` maps `background_color` to `--checkout-bg`, which is the colour
  // its whole page is painted with. Emitting only the live ten would not fail a build, a test or a
  // deploy: it would silently stop setting a variable, and the page would fall back to whatever its
  // `:root` block happens to say. The legacy entries come out of this loop when the last consumer
  // stops naming them, not before.
  // ⚠️ LEGACY first, then the live roles — the order is load-bearing and was the other way round
  // for one commit. Two fields can want the same variable: `checkout` maps `white_color` to
  // `--x-text`, and the retired `text_color` derives to `--x-text` by default. Whichever is written
  // last wins, so with the live roles first a page got its colour from the RETIRED name. Identical
  // in production, because one derives from the other — and a silent fallback the moment a record
  // carries only the ten live roles, which is every record the console saves from now on.
  for (const field of [...LEGACY_COLOR_FIELDS, ...COLOR_FIELDS]) {
    const role = field.replace(/_color$/, '').replace(/_/g, '-');
    // The FALLBACK is always looked up by the derived role name, whatever the variable is called: a
    // stylesheet's spelling is a fact about that app, and the neutral palette is a fact about the
    // fleet. Keeping them on one key is what stops `--x-bg` falling through to black.
    variables[`--${prefix}-${colors[field] ?? role}`] = colour(source?.[field], fallback(role));
  }
  // What goes ON the primary, derived from the fill rather than from the palette — so a tenant with a
  // bright accent gets legible buttons without anybody editing a colour. Read back off the answer by
  // whatever the three variables are CALLED here, so a renamed background is still the background.
  const named = (field: string, role: string): string =>
    variables[`--${prefix}-${colors[field] ?? role}`] ?? fallback(role);
  // What goes ON the primary. Reads the LIVE roles — the dark and the white — rather than the
  // retired `background`/`text` pair it used until 2026-10-03. Same two colours either way for an
  // unmigrated brand, since those are exactly what they derive from.
  variables[`--${prefix}-on-primary`] = readableOn(
    named('primary_color', 'primary'),
    named('dark_color', 'dark'),
    named('white_color', 'white'),
  );
  // The three DERIVED surfaces. A card is no longer a role — see the lifts above — so every page
  // gets `--<prefix>-surface`, `--<prefix>-surface-alt` and `--<prefix>-hairline` whether or not it
  // maps anything, and a stylesheet that wants a panel asks for one instead of reaching for a
  // retired name. `surfaces` renames them for an app whose stylesheet already says something else.
  const dark = named('dark_color', 'dark');
  const white = named('white_color', 'white');
  const grey = named('grey_color', 'grey');
  const derived: Record<string, string> = {
    surface: mix(dark, white, SURFACE_LIFT),
    'surface-alt': mix(dark, white, SURFACE_ALT_LIFT),
    'background-alt': mix(dark, white, BACKGROUND_ALT_LIFT),
    hairline: mix(dark, grey, HAIRLINE_LIFT),
  };
  for (const [role, value] of Object.entries(derived)) {
    variables[`--${prefix}-${surfaces[role] ?? role}`] = value;
  }
  for (const [name, role] of Object.entries(fonts)) {
    // An empty name writes the bare `--<prefix>-font`. See `FontAliases`.
    variables[`--${prefix}-font${name ? `-${name}` : ''}`] = stackFor(source?.fonts, role);
  }
  return variables;
}

/**
 * The `@font-face` rules for the faces a brand publishes, as a stylesheet string.
 *
 * `font-display: swap`, so the first paint uses the fallback and re-renders when the file lands rather
 * than holding the page blank over a typeface. WOFF2 first, TTF second — see {@link fontSources}. The files are served from this app's own CDN with
 * `access-control-allow-origin: *` — one fewer third party in the critical path, and the reason a page
 * never builds a `fonts.googleapis.com` url out of a slug.
 *
 * Each family is emitted once even when two roles share it, and each url is checked before it is
 * written into a stylesheet.
 */
export function brandFontFaceCss(fonts: BrandFonts | null | undefined): string {
  const rules: string[] = [];
  const seen = new Set<string>();
  for (const role of ['primary', 'secondary', 'mono'] as const) {
    const font = fonts?.[role];
    if (!font) continue;
    const family = (font.family ?? '').trim();
    if (!family || !/^[\w\s-]{1,48}$/.test(family) || seen.has(family)) continue;
    seen.add(family);
    for (const [weight, url] of Object.entries(font.faces ?? {})) {
      if (!/^\d{3}$/.test(weight)) continue;
      if (!/^https:\/\/[\w.-]+\/[\w./-]{1,240}$/.test(url)) continue;
      rules.push(
        `@font-face{font-family:'${family}';font-style:normal;font-weight:${weight};` +
          `font-display:swap;src:${fontSources(url)};}`,
      );
    }
  }
  return rules.join('\n');
}

/**
 * The `src` list for one face: the WOFF2 beside the TTF first, then the TTF (PERFORMANCE, 2026-10-09).
 *
 * The brand's font tree is TTF because Pillow renderers cannot open anything else, and it was what
 * every browser downloaded too — about 15 MB across the catalogue. `scripts/woff2.py` writes a
 * `.woff2` beside every `.ttf` (the same face at about a third of the bytes), and a browser takes
 * the first source it can load and decode, so a WOFF2 that is missing or unreadable costs one
 * failed request and falls through to the TTF. Keep the TTF second: it is that fallback.
 */
export function fontSources(url: string): string {
  const ttf = `url('${url}') format('truetype')`;
  return url.endsWith('.ttf') ? `url('${url.slice(0, -4)}.woff2') format('woff2'),${ttf}` : ttf;
}

/**
 * Put the brand on the document: the variables on `<html>`, the faces in one `<style>`.
 *
 * On `<html>` rather than on a wrapper so portalled and fixed elements inherit them too — a modal in a
 * portal is outside every wrapper a page has.
 *
 * Safe to call repeatedly, which is what paint-then-revalidate does: it is called once with the cached
 * brand before the first request and again with the answer. The stylesheet is rewritten only when it
 * actually changes, so re-applying an identical theme does not re-trigger a font load.
 */
export function applyBrandTheme(
  source: ThemeSource | null | undefined,
  options: ThemeOptions = {},
): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const [name, value] of Object.entries(brandVariables(source, options))) {
    root.style.setProperty(name, value);
  }
  const css = brandFontFaceCss(source?.fonts);
  if (!css) return;
  const id = options.styleId ?? `${options.prefix ?? 'brand'}-fonts`;
  const existing = document.getElementById(id);
  const style = existing instanceof HTMLStyleElement ? existing : document.createElement('style');
  if (style.textContent === css) return;
  style.id = id;
  style.textContent = css;
  if (!existing) document.head.appendChild(style);
}
